import React from "react";
import {useParams} from "react-router-dom";
import {RouteWithId} from "../routes/types";
import useProfileAndProposals from "../hooks/useProfileAndProposals";

import {EmptyStateContainer, Icon, PresentationPaneHeader} from "../components";
import AddStrategyButtonDropdown from "./components/AddStrategyButtonDropdown";
import ScrollableContainer from "../components/ScrollableContainer/ScrollableContainer";
import StrategySummarySection from "./components/StrategySummarySection";
import StrategyDetailsSection from "./components/StrategyDetailsSection";
import {COLOR_NT_AQUA_800} from "../constants/colors";
import {StateInputDto, StrategyDetailsSectionInfo, WealthPOSummaryResponse} from "./WealthPOTypes";
import {GTGTExemption} from "../WealthTransfer/components/EstateTaxRunningTotal";
import {buildGTGTExemptionsList} from "./WealthPOUtils";
import {EstateTaxBreakdownReportContent} from "./components/EstateTaxBreakdownContent";
import useAnalytics from "src/utils/useAnalytics";

interface WealthPlanOptimizerContentProps {
    refreshStrategiesSummary: (selectedState?: string) => void;
    listOfStates: StateInputDto[];
    strategyDetailsList: StrategyDetailsSectionInfo[];
    isContinued: boolean;
    isInReportPreview: boolean;
    wealthPOSummaryResponse: WealthPOSummaryResponse,
    estateTaxBreakdownReportContent?: EstateTaxBreakdownReportContent;
}

const WealthPlanOptimizerContent: React.FC<WealthPlanOptimizerContentProps> = ({
                                                                                   refreshStrategiesSummary,
                                                                                   listOfStates,
                                                                                   strategyDetailsList,
                                                                                   isContinued,
                                                                                   isInReportPreview,
                                                                                   wealthPOSummaryResponse,
                                                                                   estateTaxBreakdownReportContent
                                                                               }: WealthPlanOptimizerContentProps) => {
    const {id} = useParams<RouteWithId>();
    const displayName = useProfileAndProposals(id).approvedProfile.displayName;
    const sorStrategyId = wealthPOSummaryResponse.stateOfResidencyStrategy?.id;
    const gtgtExemptions: GTGTExemption[] = buildGTGTExemptionsList(wealthPOSummaryResponse.giftToGrantorTrustStrategies);

    useAnalytics(
        "Navigate_Wealth_Plan_Optimizer_Form",
        "Navigate_Wealth_Plan_Optimizer_Form_TimeSpent",
        "enableWPOptimizerGoogleAnalytics"
    );

    const showEmptyState = () => {
        const hasStrategies = strategyDetailsList.length > 0
            || estateTaxBreakdownReportContent?.showStateTaxBreakdown
            || estateTaxBreakdownReportContent?.showFederalTaxBreakdown;

        return !hasStrategies && !sorStrategyId;
    };

    return (
        <div className="wealth-plan-optimizer">
            <ScrollableContainer id={"wealth-plan-optimizer-scroll-container"}>
                <div className={showEmptyState() ? "empty-state-page" : "populated-page"}>
                    <div id="wealth-plan-optimizer-header" data-testid="wealth-plan-optimizer-header">
                        <PresentationPaneHeader
                            displayName={displayName}
                            title={isContinued ? "Wealth Plan Optimizer (continued)" : "Wealth Plan Optimizer"}
                            titleRow={<AddStrategyButtonDropdown className={"dropdown-button--primary--inverse"}/>}
                        />
                    </div>
                    {
                        showEmptyState()
                        && <div className={"no-strategy-container"}>
                            <EmptyStateContainer
                                className="no-strategy-container"
                                title="Begin by adding a strategy"
                                hideBorder={true}
                                description={<AddStrategyButtonDropdown className="add-strategy-dropdown"/>}
                            />
                        </div>
                    }
                    {
                        !showEmptyState()
                        && <div className={"wealthpo-summary-container"} data-testid={"wealthpo-summary-container"}>
                            {!isContinued && <StrategySummarySection listOfStates={listOfStates}
                                                                     isInReportPreview={isInReportPreview}
                                                                     refreshStrategiesSummary={refreshStrategiesSummary}
                                                                     wealthPOSummaryResponse={wealthPOSummaryResponse}/>}
                            <StrategyDetailsSection refreshStrategiesSummary={refreshStrategiesSummary}
                                                    strategyDetailsList={strategyDetailsList}
                                                    isContinued={isContinued}
                                                    isInReportPreview={isInReportPreview}
                                                    estateTaxBreakdownWithoutStrategies={wealthPOSummaryResponse.estateTaxBreakdownWithoutStrategies}
                                                    estateTaxBreakdownWithStrategies={wealthPOSummaryResponse.estateTaxBreakdownWithStrategies}
                                                    sorDescription={wealthPOSummaryResponse.stateOfResidencyStrategy?.enabled
                                                        ? wealthPOSummaryResponse.stateOfResidencyStrategy.description
                                                        : undefined}
                                                    gtgtExemptions={gtgtExemptions}
                                                    estateTaxBreakdownReportContent={isInReportPreview ? estateTaxBreakdownReportContent : undefined}
                            />
                            <footer className="amount-to-beneficiaries-disclaimer">
                                <Icon name="info_outline" color={COLOR_NT_AQUA_800}/>
                                <span className="amount-to-beneficiaries-disclaimer-text">
                                    The "Amount to Beneficiaries" is assumed to be invested at the time of transfer and grows at the Risk Asset rate until the end of the planning period.
                                </span>
                            </footer>
                        </div>
                    }
                </div>
            </ScrollableContainer>
        </div>
    )
}

export default WealthPlanOptimizerContent;